import { ChevronRight } from 'lucide-react'
import { useTranslation } from '@kostbar/i18n'
import {
  CELL,
  HEAD,
  Money,
  Section,
  TABLE,
  TOTAL,
  TOTAL_CELL,
} from './accounting-sections'
import type { ClosingCloseRow } from '@/features/reports/api/use-closing-report-query'
import { closeStartLabel } from '@/features/reports/lib/accounting-labels'
import {
  formatCurrency,
  formatShortDateTime,
  formatWeekdayDateTime,
} from '@/features/reports/lib/format'
import { cn } from '@/lib/utils'

/**
 * The Tagesabschlüsse of the period, one row per close (CHG-2026-10-accounting-close-periods,
 * § UI): when it was closed, its window, what its Belege recorded by tender and the online revenue
 * paid in its window, with a sum row.
 *
 * A row is a link into that close: clicking it — or its date, which is a real button for the
 * keyboard — selects the Tag of that close and keeps the Ansicht.
 */
export function CloseList({
  closes,
  onSelect,
}: {
  closes: Array<ClosingCloseRow>
  onSelect: (ref: string) => void
}) {
  const { t } = useTranslation('reports')
  const sum = closes.reduce(
    (acc, c) => ({
      cash: acc.cash + c.recorded.cashCents,
      card: acc.card + c.recorded.cardCents,
      voucher: acc.voucher + c.recorded.voucherCents,
      total: acc.total + c.recorded.totalCents,
      online: acc.online + c.onlineCents,
    }),
    { cash: 0, card: 0, voucher: 0, total: 0, online: 0 },
  )
  return (
    <Section
      title={t('accounting.closes.title')}
      unit={t('accounting.closes.unit')}
    >
      <div className="overflow-x-auto">
        <table className={TABLE}>
          <thead>
            <tr className="border-b border-border text-muted-foreground">
              <th scope="col" className={cn(HEAD, 'text-left')}>
                {t('accounting.closes.columns.closedAt')}
              </th>
              <th scope="col" className={cn(HEAD, 'text-left')}>
                {t('accounting.closes.columns.window')}
              </th>
              <th scope="col" className={cn(HEAD, 'text-right')}>
                {t('accounting.closes.columns.cash')}
              </th>
              <th scope="col" className={cn(HEAD, 'text-right')}>
                {t('accounting.closes.columns.card')}
              </th>
              <th scope="col" className={cn(HEAD, 'text-right')}>
                {t('accounting.closes.columns.voucher')}
              </th>
              <th scope="col" className={cn(HEAD, 'text-right')}>
                {t('accounting.closes.columns.recorded')}
              </th>
              <th scope="col" className={cn(HEAD, 'text-right')}>
                {t('accounting.closes.columns.online')}
              </th>
              <th scope="col" className="w-6" data-print-hide />
            </tr>
          </thead>
          <tbody>
            {closes.map((row) => (
              <tr
                key={row.ref}
                className="cursor-pointer hover:bg-muted"
                onClick={() => onSelect(row.ref)}
              >
                <td className={cn(CELL, 'whitespace-nowrap')}>
                  <button
                    type="button"
                    className="text-left tabular-nums hover:underline"
                    aria-label={t('accounting.closes.open', {
                      at: formatWeekdayDateTime(row.closedAt),
                    })}
                    onClick={(event) => {
                      event.stopPropagation()
                      onSelect(row.ref)
                    }}
                  >
                    {formatWeekdayDateTime(row.closedAt)}
                  </button>
                  <CloseMarker close={row} />
                </td>
                <td
                  className={cn(
                    CELL,
                    'whitespace-nowrap text-xs tabular-nums text-muted-foreground',
                  )}
                >
                  {closeStartLabel(t, row.from, formatShortDateTime)} →{' '}
                  {formatShortDateTime(row.closedAt)}
                </td>
                <Money value={row.recorded.cashCents} />
                <Money value={row.recorded.cardCents} />
                <Money value={row.recorded.voucherCents} />
                <td
                  className="px-4 py-1 text-right font-semibold tabular-nums"
                  data-print-tight
                >
                  {formatCurrency(row.recorded.totalCents)}
                </td>
                <Money value={row.onlineCents} />
                <td className="pr-2 text-muted-foreground" data-print-hide>
                  <ChevronRight className="h-4 w-4" aria-hidden="true" />
                </td>
              </tr>
            ))}
            <tr className={TOTAL}>
              <td className={TOTAL_CELL} colSpan={2}>
                {t('accounting.closes.sumRow', { count: closes.length })}
              </td>
              <Money value={sum.cash} total />
              <Money value={sum.card} total />
              <Money value={sum.voucher} total />
              <Money value={sum.total} total />
              <Money value={sum.online} total />
              <td data-print-hide />
            </tr>
          </tbody>
        </table>
      </div>
    </Section>
  )
}

/** "über Monatswechsel" or "3 Tage" on a close where it applies; nothing on an ordinary one. */
export function CloseMarker({ close }: { close: ClosingCloseRow }) {
  const { t } = useTranslation('reports')
  if (close.crossesMonth) {
    return (
      <span className="ml-1.5 rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-semibold text-primary">
        {t('accounting.monthBoundary')}
      </span>
    )
  }
  if (close.daySpan > 1) {
    return (
      <span className="ml-1.5 rounded-full bg-amber-500/15 px-2 py-0.5 text-[11px] font-semibold text-amber-700 dark:text-amber-400">
        {t('accounting.days', { count: close.daySpan })}
      </span>
    )
  }
  return null
}
